import type { CSSProperties, ReactNode } from 'react';
import styles from './ui.module.css';
import { Icon, ICON_LABELS, type IconName } from './Icon';

export { Icon, ICON_LABELS };
export type { IconName };

const cx = (...names: (string | false | undefined)[]) =>
  names.filter(Boolean).join(' ');

type Tone = 'light' | 'dark';

/**
 * Props para a animação de entrada (ver PageEffects). `index` escalona itens de uma lista.
 * Ex.: `<article {...reveal(i)}>`.
 */
export function reveal(index = 0, step = 80, onLoad = false) {
  return {
    'data-reveal': onLoad ? 'load' : '',
    style: {
      '--reveal-delay': `${Math.min(index, 8) * step}ms`,
    } as CSSProperties,
  };
}

export function Eyebrow({
  children,
  tone = 'light',
}: {
  children: ReactNode;
  tone?: Tone;
}) {
  return (
    <span className={cx(styles.eyebrow, tone === 'dark' && styles.eyebrowDark)}>
      {children}
    </span>
  );
}

type SectionHeadingProps = {
  eyebrow: string;
  title: ReactNode;
  lead?: ReactNode;
  tone?: Tone;
  align?: 'center' | 'start';
  as?: 'h1' | 'h2';
};

export function SectionHeading({
  eyebrow,
  title,
  lead,
  tone = 'light',
  align = 'center',
  as: Tag = 'h2',
}: SectionHeadingProps) {
  return (
    <div
      className={cx(styles.heading, align === 'start' && styles.headingStart)}
      data-reveal=''
    >
      <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      <Tag className={cx(styles.title, tone === 'dark' && styles.titleDark)}>
        {title}
      </Tag>
      {lead && (
        <p className={cx(styles.lead, tone === 'dark' && styles.leadDark)}>
          {lead}
        </p>
      )}
    </div>
  );
}

export type ButtonVariant =
  'primary' | 'accent' | 'light' | 'outline' | 'ghost';

type ButtonLinkProps = {
  href: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: 'md' | 'lg';
  block?: boolean;
  className?: string;
  external?: boolean;
};

export function buttonClass(
  variant: ButtonVariant = 'primary',
  size: 'md' | 'lg' = 'md',
  block = false,
  className?: string,
) {
  return cx(
    styles.btn,
    styles[variant],
    size === 'lg' && styles.lg,
    block && styles.block,
    className,
  );
}

export function ButtonLink({
  href,
  children,
  variant,
  size,
  block,
  className,
  external,
}: ButtonLinkProps) {
  return (
    <a
      href={href}
      className={buttonClass(variant, size, block, className)}
      {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </a>
  );
}

export function IconChip({ name, small }: { name: IconName; small?: boolean }) {
  return (
    <span className={cx(styles.iconChip, small && styles.iconChipSm)}>
      <Icon name={name} size={small ? 16 : 19} title={ICON_LABELS[name]} />
    </span>
  );
}

export function DarkBand({
  className,
  children,
  id,
}: {
  className?: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <div id={id} className={cx(styles.darkBand, className)}>
      <div className={styles.gridOverlay} aria-hidden />
      {children}
    </div>
  );
}

export function Panel({
  className,
  children,
  id,
  as: Tag = 'section',
}: {
  className?: string;
  children: ReactNode;
  id?: string;
  as?: 'section' | 'div';
}) {
  return (
    <Tag id={id} className={cx(styles.panel, className)}>
      {children}
    </Tag>
  );
}

export function FloatCard({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <div className={cx(styles.floatCard, className)}>{children}</div>;
}

export function Metric({
  label,
  value,
  delta,
}: {
  label: string;
  value: string;
  delta?: string;
}) {
  return (
    <>
      <span className={styles.floatLabel}>{label}</span>
      <span className={styles.floatValue}>
        {value}
        {delta && <span className={styles.delta}>{delta}</span>}
      </span>
    </>
  );
}

export function Sparkline() {
  return (
    <svg
      width='100%'
      height='40'
      viewBox='0 0 200 40'
      preserveAspectRatio='none'
      aria-hidden
    >
      <path
        d='M0 34 L25 30 L50 32 L75 22 L100 25 L125 15 L150 18 L175 8 L200 6 L200 40 L0 40 Z'
        style={{ fill: 'var(--blue-50)' }}
      />
      <path
        d='M0 34 L25 30 L50 32 L75 22 L100 25 L125 15 L150 18 L175 8 L200 6'
        fill='none'
        style={{ stroke: 'var(--blue)' }}
        strokeWidth='2.2'
      />
    </svg>
  );
}

export { styles as uiStyles, cx };
